import React, {
  useRef,
  useImperativeHandle,
  useState,
  forwardRef,
} from 'react';
import { observer } from 'mobx-react';
import { useContextManager } from 'apps/context/hooks';
import { useChatControl } from 'libs/rendering/hooks';
import { useRouterStore } from 'apps/hooks';

import { Virtuoso } from 'react-virtuoso';
import { Spinner } from 'components';

import ChatMessage from './chatMessage';
import EmptyChat from './emptyChat';

import {
  ChatViewportWrapper,
  ChatContentWrapper,
  SpinnerWrapper,
} from './chat.styled';
import ChatRevisions from '../ChatRevisions';

const Chat = (props, ref) => {
  const chatControl = useChatControl();
  const contextManager = useContextManager();
  const { messages } = contextManager;
  const routerStore = useRouterStore();
  const { route } = routerStore;
  const { viewerControl } = props;
  const listRef = useRef({});
  const chatRef = useRef(null);

  const scrollToBottom = options => {
    if (
      messages.length > 0 &&
      listRef?.current &&
      listRef?.current?.scrollToIndex
    ) {
      listRef.current.scrollToIndex({ index: messages.length - 1 });
      if (options.setRead) {
        contextManager.setReadMessage(contextManager.lastMessage?.id);
        contextManager.unreadCount = 0;
      }
    }
  };

  const [visibleRange, setVisibleRange] = useState({
    startIndex: contextManager?.firstMessageIndex,
    endIndex: contextManager?.lastMessageIndex,
  });

  useImperativeHandle(ref, () => ({
    getLastReadMessage() {
      return messages[visibleRange.endIndex];
    },
    scrollToBottom() {
      scrollToBottom();
    },
  }));

  const { revisions } = contextManager;
  const areThereRevisions = revisions && revisions.length;
  // Tried with react-window, but it seems to have problems with different
  // sizes. Virtuoso seems to solve those problems
  // https://github.com/bvaughn/react-window/issues/590
  // https://github.com/petyosi/react-virtuoso

  const isSharedFile = route.name.includes('shared');
  return (
    chatControl.chatVisible && (
      <ChatViewportWrapper>
        <ChatContentWrapper
          hasPaddingTop={areThereRevisions}
          id='chat-wrapper'
          ref={chatRef}
        >
          {areThereRevisions ? <ChatRevisions /> : null}
          {messages &&
          messages.length > 0 &&
          contextManager &&
          contextManager.members ? (
            <Virtuoso
              style={{
                width: '100%',
                height: isSharedFile ? 'calc(100% - 40px)' : '100%',
              }}
              ref={listRef}
              firstItemIndex={contextManager.firstMessageIndex}
              initialTopMostItemIndex={999999}
              data={messages}
              rangeChanged={setVisibleRange}
              followOutput
              // TODO: Define this followOutput politics
              // By default, it will always scroll down
              startReached={() => {
                contextManager.LoadPreviousMessageBatch();
              }}
              context={{
                lastReadMessage: contextManager.lastReadMessage,
                members: contextManager.members,
              }}
              itemContent={(index, message, { lastReadMessage, members }) => {
                let createdBy = null;
                if (typeof message.createdBy === 'string') {
                  createdBy = members[message.createdBy];
                } else {
                  createdBy = message.createdBy.profile;
                }
                let isLastRead = false;
                if (message.id && message.id === lastReadMessage?.id) {
                  isLastRead = true;
                }

                let isLastMessage =
                  messages[messages.length - 1].id === message.id;
                if (!message.id) {
                  isLastMessage =
                    messages[messages.length - 1].tempKey === message.tempKey;
                }

                return (
                  <div>
                    <ChatMessage
                      key={`message-${message.id}` || `pending-${index}`}
                      index={index}
                      message={message}
                      createdBy={createdBy}
                      isLastRead={isLastRead && !isLastMessage}
                      onScrollToBottom={scrollToBottom}
                      canDelete={contextManager.context?.canDeleteMessage(
                        message,
                      )}
                      followOutput={true}
                      onDelete={() => contextManager.DeleteMessage(message.id)}
                      viewerControl={viewerControl}
                    />
                  </div>
                );
              }}
            />
          ) : contextManager.conversationQuery?.loading ? (
            <SpinnerWrapper>
              <Spinner size='large' />
            </SpinnerWrapper>
          ) : (
            <EmptyChat />
          )}
        </ChatContentWrapper>
      </ChatViewportWrapper>
    )
  );
};

export default observer(forwardRef(Chat));
